// ═══════════════════════════════════════════════════════════════
// earnings — what the work actually made, over data the app
// already has.
//
// This is the renewal argument made by the product instead of by a
// salesperson: "this month, 14 jobs, $8,400, average ticket $600."
// A mechanic who can see that number has an answer to "is this
// subscription worth it" that doesn't depend on remembering.
//
// Two decisions worth knowing, because they change the numbers:
//
//   1. Revenue is counted on the day the money CAME IN (paidAt),
//      not the day the job was invoiced or scheduled. A job invoiced
//      in January and paid in March is March revenue. That's cash
//      accounting, which is how a solo operator actually thinks
//      about a month, and it means the figure never counts money
//      that hasn't arrived.
//
//   2. An ARCHIVED customer's revenue still counts. The work
//      happened and was paid for; archiving hides someone from your
//      lists, it doesn't rewrite last year's earnings.
// ═══════════════════════════════════════════════════════════════
import {
  toDate, isFullyClosed, computeLaborSubtotal, computePartsSubtotal,
} from './job-helpers.js';

// Number(null), Number(''), and Number('   ') are all 0 and all
// finite, so a job with no total would quietly count as a $0 job and
// drag the average ticket down. Anything that isn't really a number
// comes back null and the caller decides what that means.
export function toAmount(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// What a job was worth. Prefers the stored total (it's what the
// customer was actually billed, and what the invoice said), and falls
// back to re-adding the line items for older jobs written before
// totalPrice was maintained.
export function jobRevenue(job) {
  const stored = toAmount(job?.totalPrice);
  if (stored !== null) return stored;
  const labor = computeLaborSubtotal(job?.lineItems);
  const parts = computePartsSubtotal(job?.lineItems);
  const supplies = toAmount(job?.shopSupplies) ?? 0;
  return labor + parts + supplies;
}

// The day the money landed. closedAt is the fallback for jobs closed
// before paidAt was recorded separately.
export function revenueDate(job) {
  return toDate(job?.paidAt) || toDate(job?.closedAt) || null;
}

// Money in the bank: paid AND closed. Everything else is a promise.
export function isEarned(job) {
  return isFullyClosed(job) && !!revenueDate(job);
}

// ─── Periods ────────────────────────────────────────────────────
export function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
export function startOfYear(d)  { return new Date(d.getFullYear(), 0, 1); }
export function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }

export const PERIODS = [
  { value: 'month',     label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'year',      label: 'This year' },
  { value: 'all',       label: 'All time' },
];

// Returns { from, to, label, prev } where `to` is EXCLUSIVE and
// `prev` is the equivalent preceding window for a like-for-like
// comparison. `from: null` means unbounded (all time), which is the
// one period with nothing to compare against.
export function periodRange(period, now = new Date()) {
  const thisMonth = startOfMonth(now);
  switch (period) {
    case 'month':
      return {
        from: thisMonth,
        to: addMonths(thisMonth, 1),
        label: now.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
        prev: { from: addMonths(thisMonth, -1), to: thisMonth, label: 'last month' },
      };
    case 'lastMonth': {
      const from = addMonths(thisMonth, -1);
      return {
        from,
        to: thisMonth,
        label: from.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
        prev: { from: addMonths(thisMonth, -2), to: from, label: 'the month before' },
      };
    }
    case 'year': {
      const from = startOfYear(now);
      return {
        from,
        to: new Date(from.getFullYear() + 1, 0, 1),
        label: String(from.getFullYear()),
        prev: {
          from: new Date(from.getFullYear() - 1, 0, 1),
          to: from,
          label: String(from.getFullYear() - 1),
        },
      };
    }
    default:
      return { from: null, to: null, label: 'All time', prev: null };
  }
}

function inRange(date, from, to) {
  if (!date) return false;
  if (from && date < from) return false;
  if (to && date >= to) return false;
  return true;
}

// ─── The numbers ────────────────────────────────────────────────
export function summarise(jobs, { from = null, to = null } = {}) {
  const earned = (Array.isArray(jobs) ? jobs : [])
    .filter(isEarned)
    .filter((j) => inRange(revenueDate(j), from, to));

  let revenue = 0, labor = 0, parts = 0, supplies = 0;
  let minutes = 0, jobsWithMinutes = 0;

  earned.forEach((j) => {
    revenue  += jobRevenue(j);
    labor    += computeLaborSubtotal(j.lineItems);
    parts    += computePartsSubtotal(j.lineItems);
    supplies += toAmount(j.shopSupplies) ?? 0;
    const m = toAmount(j.workMinutes);
    if (m !== null && m > 0) { minutes += m; jobsWithMinutes += 1; }
  });

  const count = earned.length;
  return {
    count,
    revenue,
    labor,
    parts,
    supplies,
    // An average over zero jobs is not 0, it's nothing. Returning 0
    // would print "$0 average ticket" for a mechanic who simply
    // hasn't closed anything yet.
    averageTicket: count > 0 ? revenue / count : null,
    // Only honest over the jobs that were actually timed — a mechanic
    // who clocks half their work would otherwise see half their real
    // hourly rate.
    hours: minutes / 60,
    jobsWithMinutes,
    hourly: minutes > 0 ? revenue / (minutes / 60) : null,
    jobs: earned,
  };
}

// Percentage change, or null when there's nothing to compare to.
// Growth from zero is not "infinity percent" — it's just new.
export function change(current, previous) {
  if (previous === null || previous === undefined) return null;
  if (!Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

// Revenue per calendar month, oldest first — the shape of the year.
export function monthlySeries(jobs, months = 12, now = new Date()) {
  const first = addMonths(startOfMonth(now), -(months - 1));
  const buckets = [];
  for (let i = 0; i < months; i += 1) {
    const start = addMonths(first, i);
    buckets.push({
      start,
      key: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`,
      label: start.toLocaleDateString(undefined, { month: 'short' }),
      year: start.getFullYear(),
      revenue: 0,
      count: 0,
    });
  }
  const byKey = Object.fromEntries(buckets.map((b) => [b.key, b]));
  (Array.isArray(jobs) ? jobs : []).filter(isEarned).forEach((j) => {
    const d = revenueDate(j);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const b = byKey[key];
    if (!b) return;            // outside the window
    b.revenue += jobRevenue(j);
    b.count += 1;
  });
  return buckets;
}

// Still owed: invoiced and not yet paid. Deliberately NOT scoped to
// the period — money owed is owed now, whenever the job was done.
export function outstandingTotal(jobs) {
  return (Array.isArray(jobs) ? jobs : [])
    .filter((j) => j.status === 'invoiced')
    .reduce((sum, j) => sum + jobRevenue(j), 0);
}

// The single best month on record — worth surfacing because it's the
// number a mechanic quotes back to you.
export function bestMonth(jobs) {
  const totals = new Map();
  (Array.isArray(jobs) ? jobs : []).filter(isEarned).forEach((j) => {
    const d = revenueDate(j);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const cur = totals.get(key) || { revenue: 0, count: 0, start: new Date(d.getFullYear(), d.getMonth(), 1) };
    cur.revenue += jobRevenue(j);
    cur.count += 1;
    totals.set(key, cur);
  });
  let best = null;
  totals.forEach((v) => { if (!best || v.revenue > best.revenue) best = v; });
  return best;
}
